import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useChartColors } from "@/features/dashboard/components/chartTheme";
import { useChartReveal } from "@/features/dashboard/components/motion";
import type { ResponseTimePoint } from "../mockAnalytics";

interface ResponseTimeChartProps {
  data: ResponseTimePoint[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 p-2.5 shadow-xl backdrop-blur-sm text-xs">
      {label && <p className="mb-1.5 font-semibold text-slate-700 dark:text-slate-200">{label}</p>}
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-slate-600 dark:text-slate-400">{entry.name}</span>
            </div>
            <span className="font-bold tabular-nums text-slate-900 dark:text-white">
              {entry.value}s
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * AI vs. human average response time trend. Two independent Y-axes since AI
 * responds in single-digit seconds while human handoffs take minutes — a
 * shared axis would flatten the AI line to an invisible flat 0.
 */
export function ResponseTimeChart({ data }: ResponseTimeChartProps) {
  const colors = useChartColors();
  const { ref, revealed, isAnimationActive } = useChartReveal();

  return (
    <div ref={ref} className="h-64 md:h-72">
      {revealed && (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 18, left: -4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="opacity-40 dark:opacity-20" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: colors.mutedForeground }}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              yAxisId="ai"
              tick={{ fontSize: 11, fill: colors.mutedForeground }}
              tickLine={false}
              axisLine={false}
              width={34}
              label={{ value: "AI (sec)", angle: -90, position: "insideLeft", fontSize: 10, fill: colors.mutedForeground, offset: 10 }}
            />
            <YAxis
              yAxisId="human"
              orientation="right"
              tick={{ fontSize: 11, fill: colors.mutedForeground }}
              tickLine={false}
              axisLine={false}
              width={42}
              label={{ value: "Human (sec)", angle: 90, position: "insideRight", fontSize: 10, fill: colors.mutedForeground, offset: 10 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-slate-600 dark:text-slate-300">{value}</span>}
            />
            <Line
              yAxisId="ai"
              type="monotone"
              dataKey="aiSeconds"
              name="AI Response"
              stroke={colors.primary}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={isAnimationActive}
              animationDuration={900}
              animationEasing="ease-out"
            />
            <Line
              yAxisId="human"
              type="monotone"
              dataKey="humanSeconds"
              name="Human Response"
              stroke={colors.info}
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={isAnimationActive}
              animationDuration={900}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
